// ── useSettingsSync — настройки и свои языки между устройствами ─────────────
// При входе: отправляет то, что меняли без сети (свои языки, настройки), и
// забирает с сервера сделанное на другом устройстве. При возврате на вкладку —
// забирает ещё раз. Пришли новые настройки — onUpdated(keys, uid), чтобы App
// перечитал тему, язык и папки (остальные части слушают 'ac:settings-updated').
// Возвращает true, когда настройки с сервера уже получены (до этого не решаем,
// спрашивать ли «Какой язык учишь?»).
// window.__acSyncAddLang / __acSyncRemoveLang — для окна своих языков: оно
// открывается глубоко внутри выбора языка, без передачи через все экраны.

import { useEffect, useRef, useState } from 'react';
import { pullSettings, pushSettings, setSyncUser } from '../settings';
import { flushCustomLangsQueue, pullCustomLangsRemote, syncAddCustomLang, syncRemoveCustomLang } from '../storage';

export default function useSettingsSync(session, onUpdated) {
  const [pulled, setPulled] = useState(false);
  const onUpdatedRef = useRef(onUpdated);
  onUpdatedRef.current = onUpdated;
  useEffect(() => {
    const uid = session?.user?.id;
    setPulled(false);
    if (!uid) { setSyncUser(null); return undefined; }
    setSyncUser(uid);
    (async () => {
      try { await flushCustomLangsQueue(uid); } catch { /* в следующий раз */ }
      try { await pullCustomLangsRemote(uid); } catch { /* офлайн */ }
      // Сначала забираем (могли поменять на другом устройстве), потом отправляем
      // (могли поменять здесь без сети). pull шлёт 'ac:settings-updated'.
      try { await pullSettings(uid); } catch { /* офлайн */ }
      setPulled(true);
      try { await pushSettings(uid); } catch { /* офлайн */ }
    })();
    const onFocus = () => { pullSettings(uid).catch(() => {}); };
    const onSettingsUpdated = (ev) => onUpdatedRef.current?.(ev?.detail?.keys || [], uid);
    window.addEventListener('focus', onFocus);
    window.addEventListener('ac:settings-updated', onSettingsUpdated);
    window.__acSyncAddLang = (lang) => { syncAddCustomLang(uid, lang).catch(() => {}); };
    window.__acSyncRemoveLang = (code) => { syncRemoveCustomLang(uid, code).catch(() => {}); };
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('ac:settings-updated', onSettingsUpdated);
      delete window.__acSyncAddLang;
      delete window.__acSyncRemoveLang;
    };
  }, [session]);
  return pulled;
}
